import { apiClient } from '@/services/apiClient';

const unwrap = (r: any) => r.data.data;

export type ZernioPlatform =
  | 'twitter'
  | 'instagram'
  | 'facebook'
  | 'linkedin'
  | 'tiktok'
  | 'youtube'
  | 'pinterest'
  | 'reddit'
  | 'bluesky'
  | 'threads'
  | 'telegram';

export type PostStatus = 'all' | 'published' | 'scheduled' | 'draft' | 'failed';

export interface ZernioCredentials {
  configured: boolean;
  /** Masked (sk_abcd...1234) — the raw key never leaves the backend. */
  apiKey: string | null;
  profileId: string | null;
  connectedPlatforms: string[];
  isVerified: boolean;
}

export interface ZernioAccount {
  platform: string | null;
  id: string;
  name: string | null;
  username: string | null;
  isActive: boolean;
}

export interface ZernioPostPlatform {
  platform: string | null;
  /**
   * The connected account this target publishes to. Zernio returns an object on
   * a fetched post; the API flattens it to the id, which is the form create and
   * update expect.
   */
  accountId?: string | null;
  status: string;
  platformPostUrl: string | null;
  publishedAt?: string | null;
  error: string | null;
  errorCategory?: string | null;
}

export interface ZernioMediaItem {
  type: 'image' | 'video' | null;
  url: string | null;
  thumbnail?: string | null;
}

export interface ZernioPost {
  postId: string;
  status: string;
  content: string | null;
  title: string | null;
  scheduledFor: string | null;
  timezone?: string | null;
  createdAt: string | null;
  mediaItems?: ZernioMediaItem[];
  platforms: ZernioPostPlatform[];
}

export interface CreatePostResult {
  success: boolean;
  postId: string;
  status: string;
  message: string;
  platforms: ZernioPostPlatform[];
}

export interface QueueSlots {
  exists: boolean;
  schedule: unknown;
  nextSlots: unknown[];
}

export interface CreatePostInput {
  content: string;
  title?: string;
  platforms: Array<{
    platform: string;
    accountId: string;
    customContent?: string;
    platformSpecificData?: { subreddit?: string };
  }>;
  mediaUrls?: Array<{ type: 'image' | 'video'; url: string }>;
  coverImageUrl?: string;
  coverOffsetMs?: number;
  publishNow?: boolean;
  scheduledFor?: string;
  timezone?: string;
  queuedFromProfile?: string;
  tags?: string[];
  hashtags?: string[];
}

export function getZernioCredentials(): Promise<ZernioCredentials> {
  return apiClient.get('/zernio/credentials').then(unwrap);
}

export function saveZernioCredentials(
  apiKey: string,
): Promise<ZernioCredentials & { success: boolean; error: string | null }> {
  return apiClient.post('/zernio/credentials', { apiKey }).then(unwrap);
}

export function testZernioConnection(): Promise<{ valid: boolean; error: string | null }> {
  return apiClient.post('/zernio/test-connection').then(unwrap);
}

export function refreshZernioPlatforms(): Promise<{
  connectedPlatforms: string[];
  accounts: ZernioAccount[];
}> {
  return apiClient.post('/zernio/refresh-platforms').then(unwrap);
}

export function listZernioAccounts(): Promise<{ accounts: ZernioAccount[] }> {
  return apiClient.get('/zernio/accounts').then(unwrap);
}

export function createZernioPost(input: CreatePostInput): Promise<CreatePostResult> {
  return apiClient.post('/zernio/posts', input).then(unwrap);
}

/** One entry from Instagram's licensed audio catalog. */
export interface ZernioAudioTrack {
  audioId: string;
  title: string;
  /** The artist, or the username for an original sound — Zernio sends one or the other. */
  artist: string;
  audioType: string;
  durationInMs: number;
  previewUrl: string | null;
}

/**
 * What gets attached to a Reel.
 *
 * Volumes are percentages. Muting the original is `videoVolume: 0`, which is the
 * usual intent when a track is added to footage that already has sound.
 */
export interface ZernioAudioConfiguration {
  audioId: string;
  audioVolume: number;
  videoVolume: number;
}

/**
 * Search Instagram's catalog, through one connected Instagram account.
 *
 * The account is required rather than optional: Zernio reaches the catalog via
 * the tenant that will publish, so there is no account-less search. An empty
 * query returns what Zernio considers trending.
 */
export function searchInstagramAudio(
  zernioAccountId: string,
  q = '',
): Promise<{ audio: ZernioAudioTrack[] }> {
  return apiClient
    .get('/zernio/instagram-audio', { params: { zernioAccountId, ...(q ? { q } : {}) } })
    .then(unwrap);
}

/**
 * Re-read one track, refreshing its preview URL.
 *
 * Worth calling before a scheduled Reel goes out: preview links expire, and this
 * is also how a stored audioId is re-validated.
 */
export function refreshInstagramAudio(
  zernioAccountId: string,
  audioId: string,
): Promise<{ audio: ZernioAudioTrack }> {
  return apiClient
    .get(`/zernio/instagram-audio/${encodeURIComponent(audioId)}`, { params: { zernioAccountId } })
    .then(unwrap);
}

/**
 * Can this selection carry catalog music?
 *
 * Instagram Reels only — "Stories, images and carousels reject audioConfiguration
 * at creation" — so the option is hidden rather than shown and refused.
 *
 * Whether the ACCOUNT can use it is a different question and an unanswerable one
 * here: Zernio returns no loginMethod, so an account connected without Facebook
 * looks identical until it returns an error at publish time.
 */
export function supportsCatalogAudio(
  platforms: Array<string | null>,
  postType: PostType,
): boolean {
  return postType === 'reel' && platforms.some((p) => (p ?? '').toLowerCase() === 'instagram');
}

export function listZernioPosts(limit = 10, status: PostStatus = 'all'): Promise<{ posts: ZernioPost[] }> {
  return apiClient.get('/zernio/posts', { params: { limit, status } }).then(unwrap);
}

/**
 * A partial edit of a post Zernio has not published yet.
 *
 * Every field is optional and only what is sent is changed — Zernio treats an
 * absent key as "leave it alone". That is what lets a calendar drag send nothing
 * but scheduledFor and timezone, without resending caption and media.
 *
 * `mediaUrls: []` is NOT the same as omitting it: an empty array removes the
 * media, which is how the documentation does it.
 */
export interface UpdatePostInput {
  content?: string;
  title?: string;
  platforms?: CreatePostInput['platforms'];
  mediaUrls?: Array<{ type: 'image' | 'video'; url: string }>;
  coverImageUrl?: string;
  coverOffsetMs?: number;
  scheduledFor?: string;
  timezone?: string;
}

/**
 * Statuses our UI offers an edit for.
 *
 * Zernio permits draft, scheduled, failed, partial and cancelled. We offer the
 * narrower pair: a scheduled post that has not gone out, and a failed one worth
 * fixing and retrying. `partial` is excluded deliberately — it is already live
 * on some platforms, and an "Edit" button there reads as if it would pull those
 * down. Mirrors ZernioService::EDITABLE_STATUSES; change both together.
 */
export const EDITABLE_POST_STATUSES = ['scheduled', 'failed'];
export const DELETABLE_POST_STATUSES = ['scheduled', 'failed', 'draft'];

export function canEditPost(status: string | null | undefined): boolean {
  return EDITABLE_POST_STATUSES.includes((status ?? '').toLowerCase());
}

export function canDeletePost(status: string | null | undefined): boolean {
  return DELETABLE_POST_STATUSES.includes((status ?? '').toLowerCase());
}

/** PUT /zernio/posts/{postId} — the API re-checks status against Zernio first. */
export function updateZernioPost(postId: string, input: UpdatePostInput): Promise<ZernioPost> {
  return apiClient.put(`/zernio/posts/${encodeURIComponent(postId)}`, input).then(unwrap);
}

/**
 * DELETE /zernio/posts/{postId} — a hard delete in Zernio.
 *
 * The post stops existing rather than changing status, so it drops out of
 * listZernioPosts on the next fetch. Refetch after this resolves; there is no
 * local record to reconcile.
 */
export function deleteZernioPost(postId: string): Promise<void> {
  return apiClient.delete(`/zernio/posts/${encodeURIComponent(postId)}`).then(() => undefined);
}

export function getZernioQueueSlots(profileId?: string, count = 5): Promise<QueueSlots> {
  return apiClient.get('/zernio/queue-slots', { params: { profileId, count } }).then(unwrap);
}

// ─── Media ────────────────────────────────────────────────────────────────
//
// Zernio fetches mediaUrls[].url server side when the post publishes — days
// later for a scheduled post — so the URL has to be permanent, public and
// served with a real media content type. POST /zernio/media stores the file
// and hands back exactly that; a presigned link or a Drive/Dropbox share page
// fails inside the social network instead.

export interface ZernioMedia {
  url: string;
  type: 'image' | 'video';
  contentType: string;
  bytes: number;
  key: string;
  /** A signed display link, on PR previews whose bucket is private. Shown, never saved or sent. */
  previewUrl?: string;
}

/**
 * Mirrors ZernioMediaService::ALLOWED. Extensions included because some browsers
 * report no MIME type for .mov.
 *
 * GIF is deliberately absent. It used to be accepted here and by the API, but
 * the networks disagree about it — LinkedIn refuses a GIF outright — and the
 * failure only appeared after the post had been created, as a per-platform
 * "did not accept the post" with nothing pointing at the attachment. Rather than
 * model GIF support network by network from figures we cannot verify at runtime,
 * a GIF is refused up front everywhere.
 */
export const MEDIA_ACCEPT =
  'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,.jpg,.jpeg,.png,.webp,.mp4,.mov';

/** Images only — for the file browser once a post already has its one video. */
export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

const ALLOWED_CONTENT_TYPES: Record<string, 'image' | 'video'> = {
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'video/mp4': 'video',
  'video/quicktime': 'video',
};

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
};

/** Per-type ceilings the API enforces — checked here so a doomed upload never starts. */
export const MEDIA_MAX_BYTES: Record<'image' | 'video', number> = {
  image: 20 * 1024 * 1024,
  // Our ceiling. Each network's own (often lower) limit applies beneath it.
  video: 500 * 1024 * 1024,
};

/**
 * Best-effort content type for a picked file. Browsers report '' or
 * application/octet-stream often enough (notably .mov on Windows) that the
 * extension is a necessary fallback. The API sniffs the actual bytes, so this
 * only needs to be good enough to catch obvious mistakes early.
 */
export function resolveMediaType(file: File): { type: 'image' | 'video'; contentType: string } | null {
  const declared = (file.type || '').toLowerCase();
  if (ALLOWED_CONTENT_TYPES[declared]) {
    return { type: ALLOWED_CONTENT_TYPES[declared], contentType: declared };
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const fromExtension = EXTENSION_CONTENT_TYPES[extension];
  if (fromExtension) {
    return { type: ALLOWED_CONTENT_TYPES[fromExtension], contentType: fromExtension };
  }

  return null;
}

/** Human-readable reason this file can't be posted, or null when it's fine. */
export function validateMediaFile(file: File): string | null {
  const resolved = resolveMediaType(file);
  if (!resolved) {
    return 'Unsupported file type — use a JPEG, PNG, WebP, MP4 or MOV file. GIFs are not supported.';
  }
  if (file.size <= 0) {
    return 'That file is empty.';
  }

  const max = MEDIA_MAX_BYTES[resolved.type];
  if (file.size > max) {
    return `That ${resolved.type} is ${(file.size / 1048576).toFixed(1)} MB — the limit is ${Math.round(max / 1048576)} MB.`;
  }

  return null;
}

/**
 * Files at or below this go multipart; larger ones take the presigned S3 route
 * to stay under Lambda's 6 MB request-body cap (~4.5 MB once base64-encoded).
 *
 * Intentionally a local constant rather than an import from uploads.service:
 * the Zernio media path is self-contained so nothing here can affect the shared
 * upload helpers other features rely on.
 */
const MULTIPART_LIMIT = 4 * 1024 * 1024;

/**
 * Thrown when the browser→S3 leg fails for an infrastructural reason. Carries
 * no detail on purpose — the picker maps it to a generic message, and the real
 * cause is logged to the console for whoever is debugging.
 */
const TRANSPORT_FAILURE = 'ZERNIO_MEDIA_TRANSPORT_FAILED';

/**
 * Stage a large file straight to S3 via Zernio's own presign route, returning
 * the s3Key for /zernio/media to promote.
 */
/** Upload progress: percent, and bytes sent of the total, for a live bar. */
export type UploadProgress = (percent: number, loaded: number, total: number) => void;

async function presignAndPut(file: File, contentType: string, onProgress?: UploadProgress): Promise<string> {
  const response = await apiClient.post('/zernio/media/presign', {
    filename: file.name,
    contentType,
    size: file.size,
  });

  const presigned = response.data?.data ?? response.data ?? {};
  if (!presigned.uploadUrl || !presigned.s3Key) {
    console.error('[Zernio] presign returned no upload URL', response.data);
    throw new Error(TRANSPORT_FAILURE);
  }

  // XMLHttpRequest rather than fetch(): fetch has no upload-progress event, and
  // on a 50 MB video a bare spinner gives no idea how long is left. The request
  // itself is identical — the same presigned PUT straight to S3.
  const status = await new Promise<number>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.uploadUrl);
    // Must match the type the server signed, or S3 answers SignatureDoesNotMatch.
    xhr.setRequestHeader('Content-Type', presigned.contentType ?? contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
      };
    }
    xhr.onload = () => resolve(xhr.status);
    // A network-or-CORS failure: the browser hides the real status, as it did
    // for fetch. The likely cause (a missing bucket CORS rule for this origin)
    // goes to the console; the composer shows a generic message, since none of
    // that is actionable for staff.
    xhr.onerror = () => {
      console.error('[Zernio] media upload: browser could not reach S3 — check the bucket CORS rule for this origin');
      reject(new Error(TRANSPORT_FAILURE));
    };
    xhr.send(file);
  });

  if (status < 200 || status >= 300) {
    console.error(`[Zernio] media upload: S3 rejected the PUT with HTTP ${status}`);
    throw new Error(TRANSPORT_FAILURE);
  }

  return presigned.s3Key;
}

/**
 * Upload one image or video and get back the durable public URL to pass as
 * mediaUrls[].url. Files over 4 MB go straight to S3 on a presigned URL; the
 * rest through /zernio/media. Either way onProgress reports the bytes sent, and
 * reaching 100% means the transfer is done and the server is finishing up
 * (checking and filing the file), which takes a moment more.
 */
export async function uploadZernioMedia(
  file: File,
  onProgress?: UploadProgress,
): Promise<ZernioMedia> {
  // Prefer our resolved type over file.type: browsers report '' for .mov often
  // enough that the presign would otherwise be signed as the wrong type.
  const contentType = resolveMediaType(file)?.contentType || file.type || 'application/octet-stream';

  const form = new FormData();
  const direct = file.size > MULTIPART_LIMIT;
  if (!direct) {
    form.append('file', file);
  } else {
    form.append('s3Key', await presignAndPut(file, contentType, onProgress));
  }

  const response = await apiClient.post('/zernio/media', form, {
    // Only the small-file path sends bytes here; the direct path already
    // reported its progress and this is just the short finishing call.
    onUploadProgress: onProgress && !direct
      ? (e) => {
          if (e.total) onProgress(Math.round((e.loaded / e.total) * 100), e.loaded, e.total);
        }
      : undefined,
  });

  return response.data.data;
}

// ─── Display metadata ─────────────────────────────────────────────────────

export const PLATFORM_LABELS: Record<string, string> = {
  twitter: 'Twitter/X',
  x: 'Twitter/X',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
  bluesky: 'Bluesky',
  threads: 'Threads',
  telegram: 'Telegram',
  google_business: 'Google Business',
  snapchat: 'Snapchat',
};

export function platformLabel(platform: string | null): string {
  if (!platform) return 'Unknown';
  return PLATFORM_LABELS[platform.toLowerCase()] ?? platform;
}

/** Twitter/X is the only platform we surface a hard character cap for. */
export const TWITTER_LIMIT = 280;

export function isTwitter(platform: string | null): boolean {
  const p = (platform ?? '').toLowerCase();
  return p === 'twitter' || p === 'x';
}

export function isReddit(platform: string | null): boolean {
  return (platform ?? '').toLowerCase() === 'reddit';
}

/** Networks Zernio refuses to publish to without an image or video. */
/** Product ceiling on media per post, whatever a network itself allows. */
export const MAX_MEDIA_PER_POST = 10;

/**
 * What one network accepts on a single post.
 *
 * Counts are PER MEDIA TYPE, not a flat total — that distinction is the whole
 * point. TikTok takes 35 photos but only one video and refuses to mix the two;
 * X takes 4 images OR 1 video, not 4 arbitrary items. A flat "maxTotal" would
 * happily build a 3-image-plus-1-video TikTok post that Zernio cannot publish.
 */
export interface MediaCapability {
  maxImages: number;
  maxVideos: number;
  /** Ceiling across both types, after the per-type caps. */
  maxTotal: number;
  /** Whether images and videos may appear on the same post. */
  allowMixedMedia: boolean;
  /**
   * Largest video this network will take, already clamped to our own product
   * cap. See videoCap() for why every supported network currently reads 50 MB.
   */
  maxVideoBytes: number;
  /**
   * The duration window, in seconds. Infinity where Zernio documents no limit.
   *
   * Read the comment above videoCap first: several networks publish different
   * limits per post type and we cannot tell which type applies, so these are the
   * loosest documented figure per network.
   */
  maxVideoSeconds: number;
  minVideoSeconds: number;
}

/**
 * A network's documented video ceiling, never above our own cap.
 *
 * Per-network figures from Zernio's platform documentation (supplied
 * 2026-09-09), in MB: Instagram Feed/Reel 300 and Story 100, Facebook 4096,
 * TikTok 4096, LinkedIn 5120, Threads 1024, Pinterest 2048, Bluesky 50,
 * Telegram 50 (auto-compressed), WhatsApp 16. Zernio's own upload system takes
 * up to 5 GB, but the destination network's limit is what decides a publish.
 *
 * Our cap is 500 MB, so the networks below it bind: Bluesky and Telegram at
 * 50 MB, Instagram at 300 MB (100 MB for a Story, see POST_TYPE_MEDIA), and
 * WhatsApp at 16 MB, which is not in ZernioPlatform today but would bind the
 * moment it is. Everything documented above 500 MB is held at our cap.
 */
/**
 * Why the duration figures below are the most permissive variant per network.
 *
 * Zernio documents duration per POST TYPE, not per network: Instagram Feed is
 * 60 min but a Reel is 90 sec and a Story 60 sec; Facebook Feed is 4 hr and a
 * Story 120 sec; LinkedIn is 10 min for a personal profile and 30 min for a
 * company page; YouTube is 15 min unverified and 12 hr verified.
 *
 * We cannot tell which applies. ZernioAccount carries only platform, id, name,
 * username and isActive — nothing says whether a LinkedIn account is a person or
 * a company page, or whether a YouTube channel is verified — and the composer
 * offers no Feed/Reel/Story choice, so Zernio decides the post type itself.
 *
 * So each network takes its LOOSEST documented figure. That direction is
 * deliberate: too strict would refuse posts the network would happily accept —
 * a five-minute Instagram Feed video blocked by the 60-second Story limit — and
 * a false refusal is worse than a rare failure the network reports itself. The
 * cost is that a video inside the loose limit but over the strict one still
 * fails at the network, which is the behaviour we already had.
 *
 * Closing that gap needs the account type and post type in the data model. Until
 * then these catch the egregious cases, which is what was actually happening: a
 * 40-minute video went to LinkedIn, was accepted, and was dropped in processing.
 */
function videoCap(documentedMb: number): number {
  return Math.min(documentedMb * 1024 * 1024, MEDIA_MAX_BYTES.video);
}

/**
 * Per-network media capability, from Zernio's Media Uploads guide and the
 * individual platform docs (supplied 2026-09-02).
 *
 * Zernio exposes no capability endpoint — its API is profiles / accounts /
 * platforms / posts / queue-slots only — so these cannot be read at runtime and
 * live here beside TWITTER_LIMIT and MEDIA_REQUIRED_PLATFORMS.
 *
 * Where a network is more generous than our product cap the value is clamped to
 * 10 and the real figure noted, so nobody later "fixes" what looks like a
 * mistake: LinkedIn allows 20 images and TikTok 35.
 */
export const PLATFORM_MEDIA_LIMITS: Record<string, MediaCapability> = {
  // Instagram: 300 MB Feed/Reel (a Story is 100 MB, set in POST_TYPE_MEDIA).
  // Duration is the 60 min Feed figure rather than 90 s Reel or 60 s Story, for
  // the reason in the comment above videoCap. A carousel takes up to 10 items,
  // photos and videos mixed, all the shape of the first item (Zernio's Instagram
  // docs, checked 2026-10-09) — the only network here that takes several videos.
  instagram: {
    maxImages: 10, maxVideos: 10, maxTotal: 10, allowMixedMedia: true,
    maxVideoBytes: videoCap(300), maxVideoSeconds: 60 * 60, minVideoSeconds: 0,
  },
  // Facebook: 4 hr Feed; a Story is 120 s and indistinguishable here.
  facebook: {
    maxImages: 10, maxVideos: 1, maxTotal: 10, allowMixedMedia: false,
    maxVideoBytes: videoCap(4096), maxVideoSeconds: 4 * 60 * 60, minVideoSeconds: 0,
  },
  // LinkedIn supports 20 images; clamped to our product cap of 10. 30 min is the
  // company-page limit — a personal profile is 10 min, and nothing in
  // ZernioAccount says which this is.
  linkedin: {
    maxImages: 10, maxVideos: 1, maxTotal: 10, allowMixedMedia: false,
    maxVideoBytes: videoCap(5120), maxVideoSeconds: 30 * 60, minVideoSeconds: 0,
  },
  // TikTok supports 35 photos; clamped to 10. Photos and video cannot be mixed.
  // 3 s to 10 min, and the minimum is documented for every post type, so unlike
  // the maxima it can be enforced without qualification.
  tiktok: {
    maxImages: 10, maxVideos: 1, maxTotal: 10, allowMixedMedia: false,
    maxVideoBytes: videoCap(4096), maxVideoSeconds: 10 * 60, minVideoSeconds: 3,
  },
  // No documented video size for X, YouTube, Reddit, Google Business or
  // Snapchat, so those keep our own cap rather than a guessed figure. Infinity
  // for duration means Zernio documents none — not that anything goes; the
  // network still enforces whatever it enforces.
  twitter: {
    maxImages: 4, maxVideos: 1, maxTotal: 4, allowMixedMedia: false,
    maxVideoBytes: MEDIA_MAX_BYTES.video, maxVideoSeconds: Infinity, minVideoSeconds: 0,
  },
  x: {
    maxImages: 4, maxVideos: 1, maxTotal: 4, allowMixedMedia: false,
    maxVideoBytes: MEDIA_MAX_BYTES.video, maxVideoSeconds: Infinity, minVideoSeconds: 0,
  },
  threads: {
    maxImages: 10, maxVideos: 1, maxTotal: 10, allowMixedMedia: false,
    maxVideoBytes: videoCap(1024), maxVideoSeconds: 5 * 60, minVideoSeconds: 0,
  },
  // Bluesky's 50 MB is documented as a strict limit — it exactly meets our cap,
  // so it is the first network that would bind if our cap were raised.
  bluesky: {
    maxImages: 4, maxVideos: 1, maxTotal: 4, allowMixedMedia: false,
    maxVideoBytes: videoCap(50), maxVideoSeconds: Infinity, minVideoSeconds: 0,
  },
  pinterest: {
    maxImages: 1, maxVideos: 1, maxTotal: 1, allowMixedMedia: false,
    maxVideoBytes: videoCap(2048), maxVideoSeconds: 15 * 60, minVideoSeconds: 4,
  },
  // 12 hr is the verified-channel limit; unverified is 15 min and we cannot tell
  // which a channel is.
  youtube: {
    maxImages: 0, maxVideos: 1, maxTotal: 1, allowMixedMedia: false,
    maxVideoBytes: MEDIA_MAX_BYTES.video, maxVideoSeconds: 12 * 60 * 60, minVideoSeconds: 0,
  },
  google_business: {
    maxImages: 1, maxVideos: 0, maxTotal: 1, allowMixedMedia: false,
    maxVideoBytes: MEDIA_MAX_BYTES.video, maxVideoSeconds: Infinity, minVideoSeconds: 0,
  },
  snapchat: {
    maxImages: 1, maxVideos: 1, maxTotal: 1, allowMixedMedia: false,
    maxVideoBytes: MEDIA_MAX_BYTES.video, maxVideoSeconds: Infinity, minVideoSeconds: 0,
  },
  reddit: {
    maxImages: 10, maxVideos: 1, maxTotal: 10, allowMixedMedia: false,
    maxVideoBytes: MEDIA_MAX_BYTES.video, maxVideoSeconds: Infinity, minVideoSeconds: 0,
  },
};

/**
 * An unlisted network gets the safe floor, not the ceiling.
 *
 * Telegram is in PLATFORM_LABELS but absent from the figures above, so it lands
 * here: one item, no mixing. That is today's behaviour, so nothing regresses —
 * but it does mean Telegram will not offer multi-upload until its real limits
 * are confirmed.
 */
export const DEFAULT_MEDIA_CAPABILITY: MediaCapability = {
  maxImages: 1,
  maxVideos: 1,
  maxTotal: 1,
  allowMixedMedia: false,
  // Telegram lands here and documents 50 MB (auto-compressed above that), which
  // is exactly our cap, so the floor is correct for it rather than merely safe.
  maxVideoBytes: videoCap(50),
  // No duration documented for Telegram, so none is enforced. The floor is
  // deliberately not applied to duration: an invented ceiling would refuse valid
  // posts, the one outcome worse than a network reporting its own rejection.
  maxVideoSeconds: Infinity,
  minVideoSeconds: 0,
};

/**
 * What a post is published AS. Feed is the default everywhere.
 *
 * Only Instagram and Facebook document a post type at all; every other network
 * Zernio supports has exactly one kind of post, so the selector is hidden rather
 * than shown with a single option.
 */
export type PostType = 'feed' | 'story' | 'reel';

/**
 * Post types per platform, from Zernio's platform guides.
 *
 * An unlisted platform supports `feed` only — that is the honest default, not a
 * placeholder: the docs describe `contentType` for Instagram and Facebook and
 * for nothing else.
 */
export const POST_TYPE_SUPPORT: Record<string, PostType[]> = {
  instagram: ['feed', 'reel', 'story'],
  facebook: ['feed', 'reel', 'story'],
};

export function postTypesFor(platform: string | null): PostType[] {
  return POST_TYPE_SUPPORT[(platform ?? '').toLowerCase()] ?? ['feed'];
}

/**
 * The types every selected destination can publish.
 *
 * An INTERSECTION, because one post goes to all of them: adding TikTok to an
 * Instagram Story selection leaves only Feed, since TikTok has no Stories and
 * the same media cannot be two kinds of post at once.
 */
export function availablePostTypes(platforms: Array<string | null>): PostType[] {
  if (platforms.length === 0) return ['feed'];

  const sets = platforms.map((p) => new Set(postTypesFor(p)));
  return (['feed', 'reel', 'story'] as PostType[]).filter((t) => sets.every((set) => set.has(t)));
}

/**
 * The value for `platformSpecificData.contentType`, or undefined to omit it.
 *
 * THE TWO NETWORKS DISAGREE, and this asymmetry is the whole reason this
 * function exists rather than passing the post type through directly:
 *
 *   - Facebook declares both: `contentType: "reel"` and `"story"`.
 *   - Instagram declares only Stories. A Reel is INFERRED — "a single video
 *     publishes as a Reel with no contentType" — and `"reel"` is not a value it
 *     accepts. Sending it would be rejected.
 *
 * A consequence worth knowing: an Instagram video CANNOT be published as a feed
 * post. One video is always a Reel there. The composer says so rather than
 * offering a choice that quietly does something else.
 */
export function contentTypeFor(platform: string | null, postType: PostType): string | undefined {
  const key = (platform ?? '').toLowerCase();

  if (postType === 'story') {
    return postTypesFor(key).includes('story') ? 'story' : undefined;
  }
  // Facebook is the only platform that names a Reel explicitly.
  if (postType === 'reel' && key === 'facebook') {
    return 'reel';
  }

  return undefined;
}

/**
 * Media rules that REPLACE the platform's feed figures for a given post type.
 *
 * Only the fields that actually differ are listed; everything else falls through
 * to PLATFORM_MEDIA_LIMITS. Figures from Zernio's Instagram and Facebook guides.
 *
 * This is what the comment above videoCap was waiting for. Those feed numbers
 * are each network's LOOSEST variant precisely because the post type was unknown
 * — Instagram's 60 minutes is the Feed ceiling, useless for a 60-second Story.
 * With the type known the real limit can be applied, and a Story or Reel that
 * the network would drop during processing is refused here instead.
 */
const POST_TYPE_MEDIA: Record<string, Partial<Record<PostType, Partial<MediaCapability>>>> = {
  instagram: {
    // 100 MB / 60 s, and a single item — a Story carousel is not a thing.
    story: { maxImages: 1, maxVideos: 1, maxTotal: 1, maxVideoBytes: videoCap(100), maxVideoSeconds: 60 },
    // 300 MB / 90 s. Always exactly one video: an image cannot be a Reel.
    reel: { maxImages: 0, maxVideos: 1, maxTotal: 1, maxVideoBytes: videoCap(300), maxVideoSeconds: 90 },
  },
  facebook: {
    story: { maxImages: 1, maxVideos: 1, maxTotal: 1, maxVideoSeconds: 120 },
    // 3 to 60 seconds. The minimum is documented, so unlike most maxima it can
    // be enforced without qualification.
    reel: { maxImages: 0, maxVideos: 1, maxTotal: 1, maxVideoSeconds: 60, minVideoSeconds: 3 },
  },
};

/**
 * Post types that require a 9:16 video on this platform.
 *
 * Kept separate from the capability record because it is a shape rule rather
 * than a limit, and it is only checkable when the uploader measured the file —
 * see the tolerance in aspectRatioError().
 */
const VERTICAL_ONLY: Record<string, PostType[]> = {
  instagram: ['reel', 'story'],
  facebook: ['reel'],
};

export function requiresVerticalVideo(platform: string | null, postType: PostType): boolean {
  return (VERTICAL_ONLY[(platform ?? '').toLowerCase()] ?? []).includes(postType);
}

export function platformMediaCapability(
  platform: string | null,
  postType: PostType = 'feed',
): MediaCapability {
  const key = (platform ?? '').toLowerCase();
  const base = PLATFORM_MEDIA_LIMITS[key] ?? DEFAULT_MEDIA_CAPABILITY;
  // Optional trailing argument so every existing caller keeps the feed figures
  // it already had.
  const override = POST_TYPE_MEDIA[key]?.[postType];

  return override ? { ...base, ...override } : base;
}

/**
 * The combined capability for a post going to several accounts at once: the
 * strictest of every selected platform, field by field.
 *
 * Strictest wins because Zernio publishes ONE post to every selected account —
 * if any destination would reject the selection, the post is not publishable as
 * configured. With nothing selected the product cap applies, so the uploader is
 * not locked before accounts are chosen.
 */
export function mediaCapabilityFor(
  platforms: Array<string | null>,
  postType: PostType = 'feed',
): MediaCapability {
  if (platforms.length === 0) {
    // Nothing chosen yet (always the case for a template): allow what the most
    // generous network takes — an Instagram carousel — and let each network's
    // own rule tighten it once accounts are picked.
    return {
      maxImages: MAX_MEDIA_PER_POST,
      maxVideos: MAX_MEDIA_PER_POST,
      maxTotal: MAX_MEDIA_PER_POST,
      allowMixedMedia: true,
      maxVideoBytes: MEDIA_MAX_BYTES.video,
      maxVideoSeconds: Infinity,
      minVideoSeconds: 0,
    };
  }

  const caps = platforms.map((p) => platformMediaCapability(p, postType));
  return {
    maxImages: Math.min(...caps.map((c) => c.maxImages)),
    maxVideos: Math.min(...caps.map((c) => c.maxVideos)),
    maxTotal: Math.min(MAX_MEDIA_PER_POST, ...caps.map((c) => c.maxTotal)),
    // Mixing needs EVERY destination to allow it.
    allowMixedMedia: caps.every((c) => c.allowMixedMedia),
    // Smallest wins, for the same reason: one post goes to every destination, so
    // a video only has to be too big for one of them to sink the whole post.
    maxVideoBytes: Math.min(...caps.map((c) => c.maxVideoBytes)),
    // Narrowest window: lowest ceiling, highest floor, so the video has to
    // satisfy every destination at once.
    maxVideoSeconds: Math.min(...caps.map((c) => c.maxVideoSeconds)),
    minVideoSeconds: Math.max(...caps.map((c) => c.minVideoSeconds)),
  };
}

/** How many items the uploader should still accept, given what is already picked. */
export function remainingMediaSlots(
  items: Array<{ type: 'image' | 'video' }>,
  platforms: Array<string | null>,
): number {
  const cap = mediaCapabilityFor(platforms);
  const images = items.filter((m) => m.type === 'image').length;
  const videos = items.filter((m) => m.type === 'video').length;

  // Once a type is chosen and mixing is barred, only that type can still be added.
  const perType = images > 0 || (videos === 0 && cap.maxImages >= cap.maxVideos)
    ? cap.maxImages - images
    : cap.maxVideos - videos;

  return Math.max(0, Math.min(cap.maxTotal - items.length, perType));
}

/**
 * Why the current selection cannot be published, or null when it is fine.
 *
 * Returns a sentence for the user, naming the platform at fault — the message
 * is the only thing standing between them and a post that fails at publish
 * time, which for a scheduled post means days later.
 */
/**
 * A GIF in the selection, judged by extension.
 *
 * Extension rather than MIME because by the time media reaches the composer it
 * is a URL plus a display name — the picked File, and its type, are gone. The
 * query string is dropped first so a presigned or cache-busted URL still matches.
 *
 * This is the gate that matters even with GIF removed from the upload allowlist:
 * items already sitting in the Social Post library predate the block, and an
 * edited post can carry one too.
 */
export function isGifMedia(item: { url?: string; name?: string }): boolean {
  const path = (item.url ?? '').split(/[?#]/)[0];
  return /\.gif$/i.test(path) || /\.gif$/i.test(item.name ?? '');
}

export function mediaSelectionError(
  items: Array<{
    type: 'image' | 'video';
    url?: string;
    name?: string;
    bytes?: number;
    durationSeconds?: number;
    /** Measured at upload. 0 or absent for media seeded from an existing post. */
    width?: number;
    height?: number;
  }>,
  platforms: Array<string | null>,
  postType: PostType = 'feed',
  /**
   * What the media belongs to, for the wording when no account is chosen yet —
   * always the case for a template. There is no platform to name then, so the
   * message speaks about the template or post itself.
   */
  noun: 'post' | 'template' = 'post',
): string | null {
  if (items.length === 0) return null;
  const subject = noun === 'template' ? 'A template' : 'A post';
  const none = platforms.length === 0;

  // Ahead of the count rules: a GIF is unpublishable whatever else is selected,
  // and saying so is more useful than a limit message about the other files.
  if (items.some(isGifMedia)) {
    return 'GIF files are not supported for social posts. Remove the GIF, or replace it with a JPEG, PNG or WebP image.';
  }

  const images = items.filter((m) => m.type === 'image').length;
  const videos = items.filter((m) => m.type === 'video').length;

  // Report against the strictest platform for the rule actually broken, so the
  // message names the account the user has to drop.
  const offender = (pick: (c: MediaCapability) => boolean): string => {
    const hit = platforms.find((p) => pick(platformMediaCapability(p, postType)));
    return platformLabel(hit ?? null);
  };

  const cap = mediaCapabilityFor(platforms, postType);

  if (images > 0 && videos > 0 && !cap.allowMixedMedia) {
    return none
      ? `${subject} can't mix photos and videos. Use photos only, or a single video.`
      : `${offender((c) => !c.allowMixedMedia)} cannot mix images and videos in one post. Use images only, or a single video.`;
  }
  if (images > cap.maxImages) {
    return cap.maxImages === 0
      ? postType === 'reel'
        ? 'A Reel has to be a single video — an image cannot be published as one.'
        : `${offender((c) => c.maxImages === 0)} does not accept images. Use a video instead.`
      : none
        ? `${subject} can have at most ${cap.maxImages} photo${cap.maxImages === 1 ? '' : 's'}. Remove ${images - cap.maxImages} to continue.`
        : `${offender((c) => c.maxImages === cap.maxImages)} accepts at most ${cap.maxImages} image${cap.maxImages === 1 ? '' : 's'} per post.`;
  }
  if (videos > cap.maxVideos) {
    return cap.maxVideos === 0
      ? `${offender((c) => c.maxVideos === 0)} does not accept video. Use an image instead.`
      : none
        ? `${subject} can have only ${cap.maxVideos === 1 ? 'one video' : `${cap.maxVideos} videos`}. Remove ${videos - cap.maxVideos === 1 ? 'the extra video' : `${videos - cap.maxVideos} videos`} to continue.`
        : `${offender((c) => c.maxVideos === cap.maxVideos)} accepts at most ${cap.maxVideos} video${cap.maxVideos === 1 ? '' : 's'} per post.`;
  }
  if (items.length > cap.maxTotal) {
    return none
      ? `${subject} can have at most ${cap.maxTotal} photos or videos.`
      : `${offender((c) => c.maxTotal === cap.maxTotal)} accepts at most ${cap.maxTotal} media item${cap.maxTotal === 1 ? '' : 's'} per post.`;
  }

  // Video size, last: the count rules above describe the selection as a whole,
  // whereas this names one file. `bytes` is 0 or absent for media seeded from an
  // existing post — the size was never sent back — so an unknown size is passed
  // rather than guessed at, and the API's own ceiling remains the backstop.
  const tooBig = items.find(
    (m) => m.type === 'video' && (m.bytes ?? 0) > 0 && (m.bytes as number) > cap.maxVideoBytes,
  );
  if (tooBig) {
    const limitMb = Math.round(cap.maxVideoBytes / 1048576);
    const actualMb = ((tooBig.bytes as number) / 1048576).toFixed(1);
    return none
      ? `Videos can be up to ${limitMb} MB — this one is ${actualMb} MB. Use a smaller video.`
      : `${offender((c) => c.maxVideoBytes === cap.maxVideoBytes)} accepts videos up to ${limitMb} MB — this one is ${actualMb} MB. Use a smaller video, or remove that account.`;
  }

  // Duration. Same treatment as size: only judged when known, because it is 0
  // or absent for media seeded from an existing post. Checked here because an
  // over-length video is accepted by the network and then dropped during
  // processing — it never appears, and the error blames the wrong thing. Every
  // video is judged, since an Instagram carousel can hold several.
  const timed = items.filter((m) => m.type === 'video' && (m.durationSeconds ?? 0) > 0);
  const tooLong = timed.find((m) => (m.durationSeconds as number) > cap.maxVideoSeconds);
  if (tooLong) {
    return `${offender((c) => c.maxVideoSeconds === cap.maxVideoSeconds)} accepts videos up to ${formatDurationLimit(cap.maxVideoSeconds)} — this one is ${formatDurationLimit(tooLong.durationSeconds as number)}. Use a shorter video, or remove that account.`;
  }
  const tooShort = timed.find((m) => (m.durationSeconds as number) < cap.minVideoSeconds);
  if (tooShort) {
    return `${offender((c) => c.minVideoSeconds === cap.minVideoSeconds)} needs a video of at least ${formatDurationLimit(cap.minVideoSeconds)} — this one is ${formatDurationLimit(tooShort.durationSeconds as number)}.`;
  }

  // Carousel shape: Instagram needs every item to match the first item's aspect
  // ratio, or it refuses the post. Only judged where both were measured (an item
  // seeded from an existing post has no dimensions), with the same tolerance as
  // the 9:16 check so a re-encode's drift is not refused.
  if (items.length > 1 && platforms.some((p) => (p ?? '').toLowerCase() === 'instagram')) {
    const mismatch = carouselShapeError(items);
    if (mismatch) return mismatch;
  }

  // Shape, last of all. Only Stories and Reels care, and only where the file was
  // measured on upload.
  const vertical = platforms.filter((p) => requiresVerticalVideo(p, postType));
  if (vertical.length > 0) {
    const shaped = items.find((m) => m.type === 'video' && (m.width ?? 0) > 0 && (m.height ?? 0) > 0);
    if (shaped) {
      const ratioError = aspectRatioError(shaped.width as number, shaped.height as number);
      if (ratioError) {
        return `${platformLabel(vertical[0])} needs a vertical 9:16 video for a ${postType === 'reel' ? 'Reel' : 'Story'} — ${ratioError}`;
      }
    }
  }

  return null;
}

/**
 * Why an Instagram carousel's items are not all the shape of the first, or null.
 * Items without measured dimensions are skipped rather than guessed at.
 */
export function carouselShapeError(
  items: Array<{ width?: number; height?: number }>,
): string | null {
  const first = items[0];
  if (!first || !(first.width && first.height)) return null;
  const target = first.width / first.height;

  for (let i = 1; i < items.length; i++) {
    const { width, height } = items[i];
    if (!(width && height)) continue;
    if (Math.abs(width / height - target) > target * 0.03) {
      return `Instagram needs every item in a carousel to be the same shape as the first (${first.width}x${first.height}). Item ${i + 1} is ${width}x${height} — use one that matches, or remove it.`;
    }
  }
  return null;
}

/** Target 9:16 (0.5625). */
const VERTICAL_RATIO = 9 / 16;

/**
 * Why this video is not 9:16, or null.
 *
 * The tolerance is deliberate and fairly wide. A phone-shot 1080x1920 is exact,
 * but a re-encode lands on 1080x1918 often enough that an exact test would
 * refuse files the network accepts — and a false refusal here is worse than the
 * network's own, because ours blocks the post outright while Meta simply crops.
 * 3% covers the re-encode drift without letting a 1:1 or 16:9 file through.
 */
function aspectRatioError(width: number, height: number): string | null {
  const ratio = width / height;
  if (Math.abs(ratio - VERTICAL_RATIO) <= VERTICAL_RATIO * 0.03) return null;

  if (ratio > 1) {
    return `this one is landscape (${width}x${height}). Use a portrait video, 1080x1920 for best results.`;
  }
  return `this one is ${width}x${height}. Use a 9:16 video, 1080x1920 for best results.`;
}

/** "90 seconds", "10 minutes", "4 hours" — whichever reads cleanest. */
function formatDurationLimit(seconds: number): string {
  if (!Number.isFinite(seconds)) return 'any length';
  if (seconds < 60) return `${Math.round(seconds)} second${Math.round(seconds) === 1 ? '' : 's'}`;
  if (seconds < 3600) {
    // Whole minutes only. Rounding turned Instagram's 90-second Reel ceiling
    // into "2 minutes", which told the user they had half a minute they did not
    // have — the one direction a limit message must never be wrong in.
    if (seconds % 60 !== 0) {
      return `${Math.round(seconds)} seconds`;
    }
    const m = Math.round(seconds / 60);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  const h = Math.round((seconds / 3600) * 10) / 10;
  return `${h} hour${h === 1 ? '' : 's'}`;
}

/**
 * Recognised platform failures, mapped to something a clinic can act on.
 *
 * Zernio relays each network's own error text, which is written for whoever is
 * integrating the API — the TikTok capacity error, for instance, literally tells
 * you to "Use tiktokSettings.draft: true". That is useless to the person who just
 * pressed Publish and looks like a bug in our product.
 *
 * Ordered: the first match wins, so put specific patterns above general ones.
 */
const PLATFORM_ERROR_MESSAGES: Array<{ match: RegExp; message: (platform: string) => string }> = [
  {
    // "TikTok direct posting is at capacity right now. Use tiktokSettings.draft:
    // true to deliver via Creator Inbox, or try again in a few hours…"
    match: /at capacity|creator inbox|rate limit|too many requests/i,
    message: (p) => `${p} is not accepting posts right now. This is a limit on their side — try again in a few hours.`,
  },
  {
    match: /unauthori[sz]ed|invalid token|token expired|re-?authenticate|permission/i,
    message: (p) => `${p} rejected the post because the account needs reconnecting. Reconnect it in Zernio and try again.`,
  },
  {
    match: /duplicate/i,
    message: (p) => `${p} rejected this as a duplicate of a recent post. Change the caption or media and try again.`,
  },
  {
    match: /aspect ratio|resolution|too large|file size|unsupported (format|media)|codec|duration/i,
    message: (p) => `${p} would not accept this media. Check the image or video meets their requirements and try again.`,
  },
];

/**
 * Anything that marks a message as written for a developer rather than a user.
 *
 * An allowlist would be safer still, but it would also throw away the genuinely
 * useful plain-language errors networks do return. So: map what we recognise,
 * fall back to generic whenever the text looks technical, and only pass through
 * what is short and clean.
 */
const TECHNICAL_MARKERS = [
  /[a-z]+[A-Z][a-zA-Z]*\s*[.:]/, // camelCase identifiers: tiktokSettings.draft, mediaUrls:
  /\b(true|false|null|undefined)\b/i,
  /[{}[\]]|=>|::/, // JSON, arrays, arrows, PHP scope
  /\bhttps?:\/\//i,
  /\bHTTP\b|\b[45]\d\d\b/, // status codes
  /\b(api|endpoint|param(eter)?s?|payload|field|schema|header|token|stack|exception|sqlstate)\b/i,
];

/**
 * A platform failure, phrased for the person who pressed Publish.
 *
 * The raw text is never shown. Callers should log it instead, so the detail is
 * still available when someone has to debug a failing account.
 */
export function friendlyPlatformError(platform: string | null, raw: string | null): string {
  const label = platformLabel(platform);
  const text = (raw ?? '').trim();

  if (text === '') {
    return `${label} did not accept the post. Try again, or check the account in Zernio.`;
  }

  for (const { match, message } of PLATFORM_ERROR_MESSAGES) {
    if (match.test(text)) return message(label);
  }

  // Unrecognised: only pass it through if it reads like a sentence for a person.
  const technical = TECHNICAL_MARKERS.some((re) => re.test(text));
  if (technical || text.length > 160) {
    return `${label} did not accept the post. Try again, or check the account in Zernio.`;
  }

  return text;
}

/** The guidance line shown under the uploader before anything is wrong. */
export function mediaAllowanceHint(platforms: Array<string | null>): string {
  const cap = mediaCapabilityFor(platforms);
  if (platforms.length === 0) {
    return `Select accounts to see how many media items they accept. Up to ${MAX_MEDIA_PER_POST} per post.`;
  }
  if (cap.maxTotal === 1) {
    return 'This selection only supports one media item per post. Please select a single image or video.';
  }
  const labels = Array.from(new Set(platforms.map((p) => platformLabel(p))));
  const verb = labels.length === 1 ? 'supports' : 'support';
  return `${labels.join(', ')} ${verb} multiple media items for this post type. You can upload up to ${cap.maxImages} image${cap.maxImages === 1 ? '' : 's'}, or ${cap.maxVideos} video${cap.maxVideos === 1 ? '' : 's'} — not both.`;
}

export const MEDIA_REQUIRED_PLATFORMS = ['pinterest', 'instagram', 'tiktok', 'youtube'];

export function requiresMedia(platform: string | null): boolean {
  return MEDIA_REQUIRED_PLATFORMS.includes((platform ?? '').toLowerCase());
}
